/*
 * Role Assignment — the ONE description of what the form holds.
 *
 * These field names used to be typed out four times in HMSAdminDashboard.js
 * alone (the initial state, the seeding from the employee, and TWO identical
 * save payloads — the plain one and the unassign-warning one) plus once in the
 * admin service and four times in the backend view. Nine hand-kept copies of
 * one contract. A setting added to the form but missed in one of the save
 * copies ticked on screen, sent nothing, and came back as a red "No changes
 * detected" — which is exactly what has_offline_access did, and what
 * can_approve_templates (the Template Headquarters permission) did for its
 * entire life.
 *
 * WHY THIS LIVES IN constants/ RATHER THAN IN THE SCREEN
 * -----------------------------------------------------
 * The backend now REFUSES an assign-role body carrying a key it does not
 * recognise (employees/admin_views.py, ROLE_ASSIGNMENT_REQUEST_KEYS). That is
 * the right answer to a dropped permission — but it turns any drift between
 * these two lists from "one setting silently lost" into "every save from the
 * Role Assignment screen is rejected". So the drift has to be caught by a test
 * before it can ever reach an administrator.
 *
 * backend/employees/tests_role_assignment_contract.py reads THIS FILE and pins
 * its key set against the backend's. It can only do that because
 * docker-compose.dev.yml mounts frontend/src/constants into the backend
 * container read-only (the same rail crm/tests_vocabulary_parity.py uses). A
 * list kept anywhere else in frontend/src is unreadable from the test and the
 * gate would be decorative.
 *
 * Adding a setting is one line here plus its <input> in HMSAdminDashboard.js
 * plus one RoleAssignmentField row in the backend table. Miss the backend row
 * and the contract test fails immediately, in CI, instead of on an admin's
 * screen.
 */
export const ROLE_ASSIGNMENT_FORM_DEFAULTS = Object.freeze({
  is_supervisor: false,
  is_head_of_department: false,
  is_crm_approver: false,
  is_credit_approver: false,
  is_kyc_approver: false,
  is_financial_approver: false,
  can_approve_templates: false,
  // IMT only (divergence 07): approves the cost centre each new delivery creates.
  can_approve_cost_centres: false,
  is_delivery_reviewer: false,
  // Bunker Operations Approvals: the only group that sends a bunker delivery's
  // invoices to finance (ClickUp 2026-09-17).
  is_bunker_ops_approver: false,
  // Whole bunkers book without an approver / branch-manager / Ops-desk role
  // (2026-09-17).
  sees_all_bunker_deals: false,
  is_legal_approver: false,
  is_leave_supervisor: false,
  // The only setting whose "off" state is true: a leave supervisor starts
  // read-only and is widened to full access deliberately.
  leave_supervisor_view_only: true,
  can_manage_bunker_ports: false,
  can_manage_cargo_ports: false,
  has_full_access: false,
  has_cargo_access: false,
  has_bunkers_access: false,
  has_financial_access: false,
  can_access_all_entities: false,
  is_customer_trader: false,
  // Hedging desk: gets the risk email when an enquiry's sales and purchase
  // pricing bases disagree, and can see those enquiries. Additive only.
  is_hedging_trader: false,
  has_offline_access: false,
  // Reporting Central (ledger 123): open the area / build and share reports. Neither widens what a report shows.
  has_reporting_access: false,
  can_build_reports: false,
  can_manage_crm_partners: false,
  supervised_department_id: null,
});

// The two keys the save adds on top of the form's own contents. Named here so
// the backend drift gate can compare like with like: the request the screen
// actually sends is these two plus every key above.
export const ROLE_ASSIGNMENT_ENVELOPE_KEYS = Object.freeze([
  'employee_id',
  'new_role',
]);
